// Console shell: left nav rail + topbar. The nav order and the coming-soon marker
// are declared here; feature pages render only into <Outlet/>. Topbar shows admin
// email + role + server /v3/version + logout.

import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiFetch } from '../lib/api';
import { BrandLogo } from './BrandLogo';
import { CommandPalette } from './CommandPalette';
import { AssistantWidget } from '../features/assistant/AssistantWidget';
import type { AdminNavItem } from '../platform/adminPlatformModel';
import { visibleGroups } from '../platform/adminNavigation';

interface VersionInfo {
  service?: string;
  version?: string;
  commit?: string;
}

/** Topbar server version — GET /v3/version is public; non-fatal if it fails. */
function useServerVersion() {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  useEffect(() => {
    let alive = true;
    apiFetch<VersionInfo>('/v3/version')
      .then((r) => {
        if (alive) setInfo(r.data);
      })
      .catch(() => {
        /* version is decorative; ignore */
      });
    return () => {
      alive = false;
    };
  }, []);
  return info;
}

function isRouteActive(pathname: string, item: AdminNavItem): boolean {
  const paths = [item.to, ...(item.aliases ?? [])];
  return paths.some((path) => {
    if (path === '/') return pathname === '/';
    return pathname === path || pathname.startsWith(`${path}/`);
  });
}

function NavList({ items, pathname }: { items: AdminNavItem[]; pathname: string }) {
  return (
    <>
      {items.map((item) =>
        item.soon ? (
          <a key={item.to} className="disabled" aria-disabled="true" title="即將開放">
            <span className="nav-disabled-copy">
              <span className="nav-icon" aria-hidden="true">
                {item.icon}
              </span>
              {item.label}
            </span>
            <span className="soon-badge">即將</span>
          </a>
        ) : (
          // `end` on the index route so "/" is only active on exact match.
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            title={item.label}
            className={({ isActive }) => (isActive || isRouteActive(pathname, item) ? 'active' : undefined)}
          >
            <span className="nav-icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="nav-copy">
              <span className="nav-label">{item.label}</span>
              <span className="nav-meta">{item.meta}</span>
            </span>
          </NavLink>
        )
      )}
    </>
  );
}

export function AppShell() {
  const { user, role, logout } = useAuth();
  const version = useServerVersion();
  const location = useLocation();
  const groups = useMemo(() => visibleGroups(role), [role]);
  const activeItem = useMemo(() => {
    for (const group of groups) {
      const match = group.items.find((item) => isRouteActive(location.pathname, item));
      if (match) return match;
    }
    return null;
  }, [groups, location.pathname]);

  return (
    <div className="admin-shell">
      <nav className="admin-nav" aria-label="主導覽">
        <div className="admin-nav-brand">
          <BrandLogo size={30} />
          <span>
            <span className="brand-title">管理主控台</span>
            <span className="brand-subtitle">平台維運</span>
          </span>
        </div>
        {groups.map((group) => (
          <div className="admin-nav-group" key={group.label || '__pinned'}>
            {group.label && <div className="admin-nav-section">{group.label}</div>}
            <NavList items={group.items} pathname={location.pathname} />
          </div>
        ))}
      </nav>

      <CommandPalette groups={groups} />
      {/* 全域 AI 管理助理浮窗(對 CanAdminRead 角色可見;經 portal 掛到 body,零 mutate)。 */}
      <AssistantWidget />

      <div className="admin-body">
        <header className="admin-topbar">
          <div className="topbar-context">
            <span className="topbar-section">{activeItem?.label ?? '管理主控台'}</span>
            <span className="topbar-path">{activeItem?.meta ?? location.pathname}</span>
          </div>
          <div className="topbar-meta topbar-version">
            {version?.version && (
              <span className="mono" title={version.commit ? `commit ${version.commit}` : undefined}>
                {version.service ?? 'api'} {version.version}
                {version.commit ? ` · ${version.commit.slice(0, 8)}` : ''}
              </span>
            )}
          </div>
          <div className="topbar-meta">
            <div className="topbar-user">
              <span className="email">{user?.email ?? '管理員'}</span>
              {role && <span className="pill pill-info topbar-role">{role}</span>}
            </div>
            <button className="btn-ghost btn-sm" onClick={() => void logout()}>
              登出
            </button>
          </div>
        </header>

        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
